import React, { type ReactNode } from 'react';
import type { ProfilecardTriggerPosition, TriggerType } from '@atlaskit/profilecard/types';
import { useFlagsService } from '@atlassian/jira-flags';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { RouterActions } from '@atlassian/react-resource-router';
import { useIntl } from '@atlassian/jira-intl';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { profileCardNextSourceName } from '../../common/constants.tsx';
import type { AnalyticsData, ProfileCardBaseProps } from '../../common/types.tsx';
import { ProfileCardWrapper } from '../../common/ui/profilecard-wrapper/main.tsx';
import { getSpaActions } from '../../common/utils.tsx';
import { ProfileCardView } from './main.tsx';

export type ProfileCardProps = ProfileCardBaseProps & {
	accountId: string;
	analyticsData?: AnalyticsData;
	children: ReactNode;
	position?: ProfilecardTriggerPosition;
	testId?: string;
	trigger?: TriggerType;
	ariaHideProfileTrigger?: boolean;
	isChildComment?: boolean;
	shouldTruncate?: boolean;
};

export const ProfileCard = ({
	externalProfileClient,
	accountId,
	actions,
	analyticsData,
	children,
	position,
	testId,
	trigger,
	prepopulatedData,
	ariaHideProfileTrigger,
	isChildComment = false,
	shouldTruncate = false,
}: ProfileCardProps) => {
	const intl = useIntl();
	const { showFlag, dismissFlag } = useFlagsService();
	const currentUserAccountId = useAccountId();

	return (
		<ContextualAnalyticsData
			sourceType={SCREEN}
			sourceName={profileCardNextSourceName}
			attributes={analyticsData}
		>
			<ProfileCardWrapper externalProfileClient={externalProfileClient}>
				{currentUserAccountId ? (
					<RouterActions>
						{({ push }) => (
							<ProfileCardView
								accountId={accountId}
								actions={getSpaActions(accountId, intl, push, actions)}
								position={position}
								onProfileClick={(profileAccountId) => push(`/jira/people/${profileAccountId}`)}
								addFlag={showFlag}
								dismissFlag={dismissFlag}
								testId={testId}
								trigger={trigger}
								prepopulatedData={prepopulatedData}
								ariaHideProfileTrigger={ariaHideProfileTrigger}
								isChildComment={isChildComment}
								shouldTruncate={shouldTruncate}
							>
								{children}
							</ProfileCardView>
						)}
					</RouterActions>
				) : (
					children
				)}
			</ProfileCardWrapper>
		</ContextualAnalyticsData>
	);
};

export default ProfileCard;
